"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { Fragment, type JSX } from "react";
import { useTranslations } from "next-intl";
import { RichText, useSitecore } from "@sitecore-content-sdk/nextjs";

import { useArticleContext } from "contexts/ArticleContext";

import Button from "../../../atoms/Button";
import RichTextWithConsent from "../../../atoms/RichTextWithConsent";
import {
  getImageURLForCHPages,
  getLinkTargetValue,
} from "../../../utils/contentHubComponentsUtils";

const VideoIframe = dynamic(() => import("../../../atoms/VideoIframe"));

const getLegacyContent = (body: string) => {
  const pullQuoteH2Style =
    "[&_blockquote_>_h2]:text-text-gray! max-lg:[&_blockquote_>_h2]:text-[20px]! max-lg:[&_blockquote_>_h2]:leading-[28px]! lg:[&_blockquote_>_h2]:text-[24px]! lg:[&_blockquote_>_h2]:tracking-[0.8px]! lg:[&_blockquote_>_h2]:leading-[36px]!";
  const pullQuoteH3Style =
    "[&_blockquote_>_h3]:text-text-gray! max-lg:[&_blockquote_>_h3]:text-[20px]! max-lg:[&_blockquote_>_h3]:leading-[28px]! lg:[&_blockquote_>_h3]:leading-[32.16px]!";

  if (body?.includes("ecolab-youtube-video")) {
    return (
      <RichTextWithConsent
        htmlContent={body}
        className={`ch-legacy-content ${pullQuoteH2Style} ${pullQuoteH3Style}`}
      />
    );
  }
  return (
    <div
      className={`text-(--text-base) font-normal text-charcoal-black leading-6 tracking-[0.4px] lg:tracking-[0.8px] space-y-1 wrap-anywhere mb-5 ${pullQuoteH2Style} ${pullQuoteH3Style}  richtext-content ch-legacy-content`}
      dangerouslySetInnerHTML={{ __html: body }}
    ></div>
  );
};

export const Default = (): JSX.Element => {
  const { page } = useSitecore();

  const { article } = useArticleContext();

  const t = useTranslations();
  const articleAuthorPrefix = t.has("article-author-prefix")
    ? t("article-author-prefix")
    : "By";
  const referencesText = t.has("references") ? t("references") : "References";
  const aboutTheAuthorText = t.has("about-the-author-text")
    ? t("about-the-author-text")
    : "About the Author";

  console.log("---->[ArticleDetails] article", article);

  if (page.mode.isEditing) {
    return (
      <div
        className="article-details ch-details mx-auto bg-white w-full lg:w-(--article-container-width) px-(--spacing-sixteen)"
        data-role="article-details-component"
      >
        <div>
          <div>
            <h1 className="text-[28px] leading-10.5 tracking-[0.4px] font-medium text-ecolab-blue lg:text-[32px] lg:leading-12 lg:tracking-[0.8px] mt-5 mb-2">
              Article Title
            </h1>
            <h2 className="text-[20px] lg:text-2xl font-medium text-gray leading-7.5 tracking-[0.4px] lg:leading-9 lg:tracking-[0.8px] mt-5 mb-2">
              Article SubTitle
            </h2>
            <div className="mt-5 mb-5">
              <div className="text-2xl font-bold text-gray leading-[34.2897px]">
                Article Date Article Time
              </div>
            </div>
          </div>
          <div className="w-full mb-5">
            <Image
              src={"https://dummyimage.com/424x239/006ad3/fff.png"}
              alt={"Article Image"}
              width={875}
              height={493}
              className="object-cover w-full h-full rounded-md"
            />
          </div>
          <div className="text-(--text-base) lg:text-[24px] font-normal text-gray leading-6 tracking-[0.4px] lg:tracking-[0.8px] space-y-1 break-all mb-5">
            <p>Article Body</p>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return <></>;
  }

  const articleTopImageUrl = getImageURLForCHPages(
    article?.ArticleTopImage?.results?.[0]?.assetToPublicLink?.results,
  );
  const author = article?.ArticleAuthorSelection?.results?.[0];
  const authorImageUrl = getImageURLForCHPages(
    author?.AuthorImage?.results?.[0]?.assetToPublicLink?.results,
  );
  const articleMiddleImageUrl = getImageURLForCHPages(
    article?.ArticleMiddleImage?.results?.[0]?.assetToPublicLink?.results,
  );
  const articleBottomImageUrl = getImageURLForCHPages(
    article?.ArticleBottomImage?.results?.[0]?.assetToPublicLink?.results,
  );

  return (
    <Fragment>
      <div className="article-details ch-details mx-auto bg-white w-full lg:w-(--article-container-width) max-lg:px-4">
        <div>
          {article?.ArticleTitle && (
            <h1 className="text-[24px] leading-9 tracking-[0.4px] font-normal text-ecolab-blue lg:text-[32px] lg:leading-10 lg:tracking-[0.8px] mt-5 mb-2 after:content-[''] after:block after:clear-both after:pt-3 after:w-full lg:after:w-100 after:border-b-2 after:border-teal">
              {article?.ArticleTitle}
            </h1>
          )}
          {article?.ArticleSubTitle && (
            <h2 className="text-[14px] lg:text-[22px] font-normal text-neutral-900 leading-5 tracking-[0.4px] lg:leading-6 lg:tracking-[0.8px] mt-5 mb-2">
              {article?.ArticleSubTitle}
            </h2>
          )}
          <span className="flex items-center gap-3 mt-5 mb-5">
            {author?.AuthorName && (
              <h2 className="font-medium m-0 text-base text-charcoal-black leading-6 hover:underline hover:text-ecolab-blue">
                <a href="#article-author">
                  {articleAuthorPrefix} {author?.AuthorName}
                </a>
              </h2>
            )}
            {article.ArticlePostDate && (
              <div className="text-base font-normal text-neutral-900 leading-6">
                {article.ArticlePostDate}
              </div>
            )}
          </span>

          <div className="mb-8 empty:hidden">
            {article?.ArticleTopVideoUrl ? (
              <div className="w-full mb-5">
                <VideoIframe src={article?.ArticleTopVideoUrl} />
              </div>
            ) : (
              articleTopImageUrl && (
                <div className="w-full mb-5">
                  <Image
                    src={articleTopImageUrl}
                    alt={"Article top image"}
                    width={875}
                    height={493}
                    className="object-cover w-full h-auto max-h-full max-w-full rounded-md"
                    quality={75}
                    loading="eager"
                    fetchPriority="high"
                  />
                </div>
              )
            )}
            {!article?.ArticleTopVideoUrl &&
              articleTopImageUrl &&
              article?.ArticleImageCaption && (
                <figcaption className="italic text-[14px] leading-5 font-normal mt-5 tracking-[0.4px] text-gray">
                  {article?.ArticleImageCaption}
                </figcaption>
              )}

            {article?.ArticleTopHeading && (
              <h2 className="text-ecolab-blue text-[26px] font-normal leading-7 tracking-[0.4px] lg:tracking-[0.8px] mb-3.75">
                {article?.ArticleTopHeading}
              </h2>
            )}

            {article?.ArticleTopContent && (
              <div className="font-normal text-charcoal-black space-y-1 wrap-anywhere mb-5 richtext-content tracking-[0.4px] lg:tracking-[0.8px]">
                <div
                  dangerouslySetInnerHTML={{
                    __html: article?.ArticleTopContent,
                  }}
                ></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(article?.ArticlePullQuoteContent ||
        article?.ArticlePullQuoteSource ||
        article?.ArticlePullQuoteSourceTitle) && (
        <div className="bg-light-blue py-(--global-component-spacing) my-8 empty:hidden">
          <div className="mx-auto w-full lg:w-(--article-container-width) max-lg:px-4">
            {article?.ArticlePullQuoteContent && (
              <h2 className="wrap-anywhere text-[24px] leading-9 antialiased text-ecolab-blue mt-0 lg:text-[36px] lg:leading-11 tracking-normal font-normal mb-6 px-0.5 lg:px-2.5">
                &quot;{article?.ArticlePullQuoteContent}&quot;
              </h2>
            )}

            {(article?.ArticlePullQuoteSource ||
              article?.ArticlePullQuoteSourceTitle) && (
              <h3 className="wrap-anywhere text-charcoal-black text-[14px] leading-5 antialiased mt-0 tracking-normal font-normal mb-1 px-0.5 lg:px-2.5">
                &#8212;{" "}
                <span className="text-[22px] font-medium leading-6">
                  {article?.ArticlePullQuoteSource}
                </span>
                ,{" "}
                <span className="text-[22px] leading-6">
                  {article?.ArticlePullQuoteSourceTitle}
                </span>
              </h3>
            )}
          </div>
        </div>
      )}

      <div className="article-details ch-details mx-auto bg-white w-full lg:w-(--article-container-width) max-lg:px-4">
        <div className="mb-8 empty:hidden">
          {article?.ArticleMiddleVideoUrl ? (
            <div className="w-full mb-5">
              <VideoIframe src={article?.ArticleMiddleVideoUrl} />
            </div>
          ) : (
            articleMiddleImageUrl && (
              <div className="w-full mb-5">
                <Image
                  src={articleMiddleImageUrl}
                  alt={"Article middle image"}
                  width={875}
                  height={493}
                  className="object-cover w-full h-auto max-h-full max-w-full rounded-md"
                />
              </div>
            )
          )}
          {article?.ArticleMiddleHeading && (
            <h2 className="text-ecolab-blue text-[26px] font-normal leading-7 tracking-[0.4px] lg:tracking-[0.8px] mb-3.75">
              {article?.ArticleMiddleHeading}
            </h2>
          )}

          {article?.ArticleMiddleContent && (
            <div className="font-normal text-charcoal-black space-y-1 wrap-anywhere mb-5 richtext-content tracking-[0.4px] lg:tracking-[0.8px]">
              <div
                dangerouslySetInnerHTML={{
                  __html: article?.ArticleMiddleContent,
                }}
              ></div>
            </div>
          )}

          {article?.ArticleMiddleLinkCtaUrl && (
            <div className="mb-4">
              <Button
                buttonType="primary"
                tabIndex={0}
                data-role="link"
                aria-label={article.ArticleMiddleLinkCtaText}
                field={{
                  value: {
                    href: article?.ArticleMiddleLinkCtaUrl,
                    text: article.ArticleMiddleLinkCtaText,
                    target: getLinkTargetValue(
                      article?.ArticleMiddleLinkCtaUrl,
                    ),
                  },
                }}
                iconName=""
                className="inline-flex justify-center items-center m-0! ml-2.5!"
              />
            </div>
          )}
        </div>

        <div className="mb-8 empty:hidden">
          {article?.ArticleBottomVideoUrl ? (
            <div className="w-full mb-5">
              <VideoIframe src={article?.ArticleBottomVideoUrl} />
            </div>
          ) : (
            articleBottomImageUrl && (
              <div className="w-full mb-5">
                <Image
                  src={articleBottomImageUrl}
                  alt={"Article bottom image"}
                  width={875}
                  height={493}
                  className="object-cover w-full h-auto max-h-full max-w-full rounded-md"
                />
              </div>
            )
          )}

          {article?.ArticleBottomHeading && (
            <h2 className="text-ecolab-blue text-[26px] font-normal leading-7 tracking-[0.4px] lg:tracking-[0.8px] mb-3.75">
              {article?.ArticleBottomHeading}
            </h2>
          )}

          {article?.ArticleBottomContent && (
            <div className="font-normal text-charcoal-black space-y-1 wrap-anywhere mb-5 richtext-content tracking-[0.4px] lg:tracking-[0.8px]">
              <div
                dangerouslySetInnerHTML={{
                  __html: article?.ArticleBottomContent,
                }}
              ></div>
            </div>
          )}

          {article?.ArticleBottomLinkCtaUrl && (
            <div className="mb-4">
              <Button
                buttonType="primary"
                tabIndex={0}
                data-role="link"
                aria-label={article.ArticleBottomLinkCtaText}
                field={{
                  value: {
                    href: article?.ArticleBottomLinkCtaUrl,
                    text: article.ArticleBottomLinkCtaText,
                    target: getLinkTargetValue(
                      article?.ArticleBottomLinkCtaUrl,
                    ),
                  },
                }}
                iconName=""
                className="inline-flex justify-center items-center m-0! ml-2.5!"
              />
            </div>
          )}
        </div>

        {article?.References && (
          <div className="font-normal text-gray space-y-1 wrap-anywhere mb-5">
            <h3 className="text-(--text-base) lg:text-[24px] font-bold text-gray leading-6 tracking-[0.4px] lg:tracking-[0.8px]  mb-4">
              {referencesText}
            </h3>
            <div
              className="richtext-content wrap-anywhere tracking-[0.4px] lg:tracking-[0.8px]"
              dangerouslySetInnerHTML={{ __html: article?.References }}
            ></div>
          </div>
        )}

        {article?.ArticleBody && getLegacyContent(article.ArticleBody)}
      </div>

      {author && (
        <div
          className="article-details ch-details mt-12 mx-auto bg-white w-full lg:w-(--article-container-width) px-4 mb-(--global-component-spacing)"
          id="article-author"
        >
          <div className="bg-lighter-blue p-6 rounded-xl border border-lightning-blue">
            <h3 className="text-[22px] font-medium leading-6 mb-3.75 mt-0 text-charcoal-black">
              {aboutTheAuthorText}
            </h3>
            <div className="flex flex-col sm:flex-row items-left sm:items-center gap-6">
              {authorImageUrl && (
                <div className="min-w-25 overflow-hidden shadow-md self-start sm:self-center w-45 h-53.25 rounded-[40px]">
                  <Image
                    src={authorImageUrl}
                    alt={
                      author?.AuthorName ? author?.AuthorName : "Author Image"
                    }
                    width={180}
                    height={213}
                    className="object-cover w-full h-full max-h-full max-w-full"
                  />
                </div>
              )}
              <div className="flex-1">
                {author?.AuthorName && (
                  <h4 className="text-[28px] leading-8 my-0 font-normal text-teal-blue">
                    {author?.AuthorName}
                  </h4>
                )}

                {author?.AuthorRole && (
                  <p className="text-neutral-900 text-[16px] font-normal mt-0">
                    {author?.AuthorRole}
                  </p>
                )}
                {author?.AuthorDescription && (
                  <RichText
                    className={
                      "text-charcoal-black text-sm mt-4 richtext-content tracking-[0.4px] lg:tracking-[0.8px] font-normal"
                    }
                    field={{ value: author?.AuthorDescription }}
                  />
                )}

                {author?.AuthorCTAText && author?.AuthorCTAUrl && (
                  <div className="mt-4">
                    <Button
                      buttonType="primary"
                      className="inline-flex min-w-30 text-sm leading-5 font-normal justify-center items-center min-h-9! py-2! px-4! max-sm:w-full"
                      field={{
                        value: {
                          href: author.AuthorCTAUrl,
                          text: author.AuthorCTAText,
                          target: "_blank",
                          linktype: "external",
                        },
                      }}
                      tabIndex={0}
                      iconName=""
                      aria-label={author.AuthorCTAText}
                      data-role="link"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
